import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, Loader2, Smartphone, Mail, MessageSquare, KeyRound, LifeBuoy } from "lucide-react";

type Method = "totp" | "email" | "sms" | "passkey" | "backup";

interface MfaStatusResp {
  status: { enabled: boolean; enrolledMethods: ("totp" | "email" | "sms" | "passkey")[]; backupCodesRemaining: number };
  policy: { required: boolean };
  passkeys: { id: string }[];
  verified: boolean;
}

export default function MfaChallengePage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [tab, setTab] = useState<Method>("totp");

  const { data, isLoading } = useQuery<MfaStatusResp>({ queryKey: ["/api/security/mfa/status"] });

  const next = new URLSearchParams(window.location.search).get("next") || "/";

  // Available methods derived from enrolled
  const available: Method[] = (() => {
    if (!data) return [];
    const a: Method[] = [];
    for (const m of data.status.enrolledMethods) a.push(m);
    if (data.status.backupCodesRemaining > 0) a.push("backup");
    return a;
  })();

  useEffect(() => {
    if (data?.verified) {
      navigate(next);
    }
    if (available.length > 0 && !available.includes(tab)) {
      const first = available.find((a) => a !== "backup") || available[0];
      setTab(first);
    }
  }, [data, available.length]);

  const sendChallenge = useMutation({
    mutationFn: async (method: "email" | "sms") => (await apiRequest("POST", "/api/security/mfa/challenge", { method })).json(),
    onSuccess: () => toast({ title: "Code sent" }),
    onError: (e: any) => toast({ title: "Send failed", description: e.message, variant: "destructive" }),
  });

  const verify = useMutation({
    mutationFn: async (payload: { method: Method; code?: string; response?: any }) =>
      (await apiRequest("POST", "/api/security/mfa/verify", payload)).json(),
    onSuccess: () => {
      qc.invalidateQueries();
      navigate(next);
    },
    onError: (e: any) => toast({ title: "Verification failed", description: e.message, variant: "destructive" }),
  });

  const usePasskey = useMutation({
    mutationFn: async () => {
      const opts = await (await apiRequest("POST", "/api/security/mfa/passkey/auth-options", {})).json();
      const { startAuthentication } = await import("@simplewebauthn/browser");
      const response = await startAuthentication({ optionsJSON: opts });
      return (await apiRequest("POST", "/api/security/mfa/verify", { method: "passkey", response })).json();
    },
    onSuccess: () => {
      qc.invalidateQueries();
      navigate(next);
    },
    onError: (e: any) => toast({ title: "Passkey failed", description: e.message, variant: "destructive" }),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data.policy.required && data.status.enrolledMethods.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Set up MFA first</CardTitle>
            <CardDescription>You haven't added any MFA factors yet.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/account/security")} data-testid="button-go-enroll">
              Go to Security settings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Verify it's you
          </CardTitle>
          <CardDescription>Choose a verification method to finish signing in.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => setTab(v as Method)}>
            <TabsList className="grid grid-flow-col auto-cols-fr">
              {available.includes("totp") && (
                <TabsTrigger value="totp" data-testid="tab-totp"><Smartphone className="w-4 h-4" /></TabsTrigger>
              )}
              {available.includes("passkey") && (
                <TabsTrigger value="passkey" data-testid="tab-passkey"><KeyRound className="w-4 h-4" /></TabsTrigger>
              )}
              {available.includes("email") && (
                <TabsTrigger value="email" data-testid="tab-email"><Mail className="w-4 h-4" /></TabsTrigger>
              )}
              {available.includes("sms") && (
                <TabsTrigger value="sms" data-testid="tab-sms"><MessageSquare className="w-4 h-4" /></TabsTrigger>
              )}
              {available.includes("backup") && (
                <TabsTrigger value="backup" data-testid="tab-backup"><LifeBuoy className="w-4 h-4" /></TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="totp" className="space-y-3 pt-4">
              <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="input-mfa-totp-code" />
              <Button className="w-full" onClick={() => verify.mutate({ method: "totp", code })} disabled={verify.isPending || code.length < 6} data-testid="button-verify-totp">
                {verify.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Verify
              </Button>
            </TabsContent>

            <TabsContent value="email" className="space-y-3 pt-4">
              <Button variant="outline" className="w-full" onClick={() => sendChallenge.mutate("email")} disabled={sendChallenge.isPending} data-testid="button-send-email">
                Send code to my email
              </Button>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="input-mfa-email-code" />
              <Button className="w-full" onClick={() => verify.mutate({ method: "email", code })} disabled={verify.isPending || code.length < 6} data-testid="button-verify-email">
                Verify
              </Button>
            </TabsContent>

            <TabsContent value="sms" className="space-y-3 pt-4">
              <Button variant="outline" className="w-full" onClick={() => sendChallenge.mutate("sms")} disabled={sendChallenge.isPending} data-testid="button-send-sms">
                Send code by SMS
              </Button>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" data-testid="input-mfa-sms-code" />
              <Button className="w-full" onClick={() => verify.mutate({ method: "sms", code })} disabled={verify.isPending || code.length < 6} data-testid="button-verify-sms">
                Verify
              </Button>
            </TabsContent>

            <TabsContent value="passkey" className="space-y-3 pt-4">
              <p className="text-sm text-muted-foreground">Use a passkey or security key registered to this account.</p>
              <Button className="w-full" onClick={() => usePasskey.mutate()} disabled={usePasskey.isPending} data-testid="button-use-passkey">
                {usePasskey.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Use passkey
              </Button>
            </TabsContent>

            <TabsContent value="backup" className="space-y-3 pt-4">
              <p className="text-sm text-muted-foreground">Enter one of the backup codes you saved when you enrolled.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="XXXXX-XXXXX" data-testid="input-mfa-backup-code" />
              <Button className="w-full" onClick={() => verify.mutate({ method: "backup", code })} disabled={verify.isPending || code.length < 6} data-testid="button-verify-backup">
                Verify
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
